import { type ReactNode, type ComponentPropsWithoutRef } from "react"

const SHELL_LANGUAGES = new Set(["bash", "sh", "shell", "zsh"])

function isShellLanguage(className?: string): boolean {
  const language = className?.match(/language-([a-z0-9-]+)/i)?.[1]?.toLowerCase()
  return language ? SHELL_LANGUAGES.has(language) : false
}

function getTextContent(children: ReactNode): string {
  if (typeof children === "string") return children
  if (Array.isArray(children)) return children.map(getTextContent).join("")
  if (children && typeof children === "object" && "props" in children) {
    const el = children as { props: { children?: ReactNode } }
    return getTextContent(el.props.children)
  }
  return ""
}

function getCodeText(children: ReactNode): string {
  const text = getTextContent(children)
  return text.endsWith("\n") ? text.slice(0, -1) : text
}

function ShellCodeBlock({ children }: { children: ReactNode }) {
  return (
    <pre className="my-4 overflow-x-auto rounded-lg border border-default bg-raised px-3 py-2">
      <code className="flex items-start gap-3 border-0 bg-transparent p-0 text-xs text-default">
        <span className="text-muted select-none">$</span>
        <span className="min-w-0 wrap-break-word whitespace-pre-wrap">{getCodeText(children)}</span>
      </code>
    </pre>
  )
}

export function Pre({ children }: ComponentPropsWithoutRef<"pre">) {
  const codeChild = children as React.ReactElement<{
    className?: string
    children?: ReactNode
  }>
  const className = codeChild?.props?.className

  if (isShellLanguage(className)) {
    return <ShellCodeBlock>{codeChild.props.children}</ShellCodeBlock>
  }

  return (
    <pre className="my-4 overflow-x-auto rounded-lg border border-default bg-raised px-3 py-2 [&>code]:border-0 [&>code]:bg-transparent [&>code]:p-0">
      {children}
    </pre>
  )
}
